'use client';
import { useTranslations } from 'next-intl';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULE_ORDER, type PasswordRuleId } from '@/lib/password-rules';
import { CheckList } from '@/components/ui/check-list';

/** The rule copy, in one place: three forms show this checklist and none of them should be
 *  spelling the rules out again. Lives with its only consumer. */
export function usePasswordRuleLabels(): Record<PasswordRuleId, string> {
  const t = useTranslations('settings');
  return {
    minLength: t('ruleMinLength', { min: PASSWORD_MIN_LENGTH }),
    letterAndDigit: t('ruleLetterAndDigit'),
    noSequential: t('ruleNoSequential'),
    noRepeated: t('ruleNoRepeated'),
    notCurrent: t('ruleNotCurrent'),
    match: t('ruleMatch'),
  };
}

/**
 * The password rules, ticking green as the user satisfies them. A checklist instead of a
 * strength meter on purpose: a bar that says "fraca" never says WHY, and every rule here is a
 * yes/no the API enforces (`apps/api/src/common/password.ts`).
 *
 * Hidden until there is something to judge — six grey rows over an untouched field read as six
 * failures. `ids` lets a form drop rules it cannot evaluate (recovery has no current password).
 */
export function PasswordRulesChecklist({
  results,
  labels,
  ids = PASSWORD_RULE_ORDER,
  show,
  className,
}: {
  results: Record<PasswordRuleId, boolean>;
  labels: Record<PasswordRuleId, string>;
  ids?: readonly PasswordRuleId[];
  show: boolean;
  className?: string;
}) {
  if (!show) return null;
  return (
    <CheckList
      live
      items={ids.map((id) => ({ id, label: labels[id], done: results[id] }))}
      className={className}
    />
  );
}
